import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Popover,
  SvgIcon,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { withStyles } from 'tss-react/mui';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import MicIcon from '@mui/icons-material/Mic';
import VoiceRecorder, { isRecordingSupported } from 'src/components/VoiceRecorder/VoiceRecorder';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { STORAGE, MESSAGES } from 'src/constants';
import helpers from 'src/helpers';
import { isCompressibleImage } from 'src/utils/imageCompression';
import ActionMenuItem from 'src/views/management/ChatView/NewMessage/ActionMenuItem';
import { ReactComponent as UploadIcon } from 'src/assets/images/upload.svg';
import AttachmentPendingTile from 'src/components/AttachmentPendingTile';
import { contactCardMenuLabel } from 'src/helpers/contactCardAttachments';
import attachmentFieldStyle from './attachmentField.style';

const { VCARD_FILENAME } = MESSAGES;

// AT&T and some carriers reject MMS attachments over 300KB
const MMS_SIZE_LIMIT_KB = 300;

// File types that PB (Project Broadcast) texting supports - images only
const PB_ACCEPTED_FILE_TYPES = '.png,.jpg,.jpeg,.gif';

/**
 * Helper to check if an attachment is a contact card
 */
/* ***************************************************************************
 * CON-1674 — the recorder is OFF on every AttachmentField surface.
 *
 * WHY. A voice memo is only a voice memo if the server is told so. The client
 * sets isVoiceMemo on the send request, and messagesController converts the
 * recording to a 32 kbps MP3 before the message row is written. That flag
 * exists in exactly ONE place — the Connections compose box. AttachmentField
 * hands a recorded file to its parent as an ordinary attachment, so on these
 * surfaces the raw browser webm is what gets saved to the definition row
 * (scheduledActionGroupActions, actionGroupActions, parties, groups, keywords,
 * actionRules and the rest) and the raw webm is what the carrier is handed at
 * send time. No handset can play it, and the automated text it rides on is
 * charged either way.
 *
 * Making these surfaces work is not a menu item. It needs a voice-memo marker
 * carried from the field, through each definition controller, to a conversion
 * — on every table above. That is its own ticket, and the recorder stays dark
 * until it exists rather than shipping a charged send that arrives unplayable.
 *
 * Everything below is left wired so restoring it is this one flag plus the
 * server-side work that has to land with it.
 *************************************************************************** */
const VOICE_MEMO_ON_AUTOMATED_SURFACES = false;

function isContactCardAttachment(fileName) {
  return fileName === VCARD_FILENAME;
}

/**
 * Strip the datetime prefix from a filename
 * Patterns handled:
 * - 2026-01-21-16-08-15-77-originalname.pdf -> originalname.pdf
 * - EO-ID-2026-01-21-16-08-15-77-originalname.pdf -> originalname.pdf
 * The base pattern is: YYYY-MM-DD-HH-MM-SS-MS-filename
 * Order Edit uploads add "EO-ID-" prefix before the datetime
 */
function stripDatetimePrefix(fileName) {
  if (!fileName) return '';
  // Match EO-ID-YYYY-MM-DD-HH-MM-SS-MS- (Order Edit uploads)
  const eoIdPattern = /^EO-ID-\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2,3}-/;
  if (eoIdPattern.test(fileName)) {
    return fileName.replace(eoIdPattern, '');
  }
  // Match YYYY-MM-DD-HH-MM-SS-MS- (standard uploads)
  const datetimePrefixPattern = /^\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2,3}-/;
  return fileName.replace(datetimePrefixPattern, '');
}

/**
 * Get file extension from filename
 */
function getFileExtension(fileName) {
  if (!fileName) return '';
  return fileName.toLowerCase().split('.').pop();
}

/**
 * Get appropriate icon component based on file extension
 */
function getFileIcon(fileName, classes) {
  const ext = getFileExtension(fileName);

  // Image files
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
    return <ImageIcon className={classes.fileIcon} />;
  }

  // PDF files
  if (ext === 'pdf') {
    return <PictureAsPdfIcon className={classes.fileIcon} style={{ color: '#d32f2f' }} />;
  }

  // Video files
  if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', '3gp'].includes(ext)) {
    return <VideoFileIcon className={classes.fileIcon} style={{ color: '#673ab7' }} />;
  }

  // Audio files
  if (['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac', 'wma', 'amr'].includes(ext)) {
    return <AudioFileIcon className={classes.fileIcon} style={{ color: '#ff9800' }} />;
  }

  // Document files
  if (['doc', 'docx', 'txt', 'rtf'].includes(ext)) {
    return <DescriptionIcon className={classes.fileIcon} style={{ color: '#2196f3' }} />;
  }

  // Archive files
  if (['zip', 'rar', '7z'].includes(ext)) {
    return <FolderZipIcon className={classes.fileIcon} style={{ color: '#795548' }} />;
  }

  // vCard files
  if (ext === 'vcf') {
    return <ContactPageIcon className={classes.fileIcon} style={{ color: '#4caf50' }} />;
  }

  // Default file icon
  return <InsertDriveFileIcon className={classes.fileIcon} />;
}

/**
 * AttachmentField - Unified component for file attachments with Contact Card support
 *
 * Supports attaching files via file picker or attaching the user's Contact Card.
 *
 * DIRECT USE API (preferred - use this for new implementations):
 * - attachment (string): existing attachment filename, or null
 * - attachmentContainer (string): Azure container for the attachment, or null
 * - fileUpload (object): pending file upload data from Redux state
 * - onFileChange (function): callback when attachment changes
 *   Called with: { attachment, attachmentContainer, fileUpload, isContactCard }
 *   (CON-1934: a contact card chosen while there is room also carries
 *   addContactCard: true, meaning "add it, keep every file".)
 *
 * Legacy mode props (for backward compatibility with existing UploadImageField):
 * - fileName (string): existing attachment filename (use 'attachment' instead)
 * - container (string): Azure container (use 'attachmentContainer' instead)
 * - updateFileUploadToMessage (function): legacy callback for Redux dispatch
 */
function AttachmentField({
  title = 'Attachment',
  userId,
  azAccountName,
  databaseBeingUsed,
  loading = false,
  validateMessage = {},
  // Direct use API (preferred)
  attachment = null,
  attachmentContainer = null,
  onFileChange = null,
  // Legacy props for backward compatibility (deprecated)
  fileName = null,
  fileUpload = null,
  container = null,
  updateFileUploadToMessage = null,
  // Unused legacy props (kept for backward compatibility)
  value = null, // eslint-disable-line no-unused-vars
  onChange = null, // eslint-disable-line no-unused-vars
  // Common props
  compressOnSend = false,
  textingProvider = null,
  showContactCardOption = true,
  imageOnly = false,
  acceptedFileTypes = '.png,.jpg,.jpeg,.gif,.mp4,.mp3,.webm,.ogg,.wav,.flv,.mov,.wmv,.avi,.mkv,.3gp,.flac,.m4a,.aac,.wma,.amr,.pdf,.doc,.docx,.txt,.rtf,.vcf,.zip,.rar,.7z',
  errorValue = null,
  classes,
  buttonSmallText = 'Large images will be compressed. Other files must be less than 300KB.',
  onAttachmentChange = () => {},
  hideSizeBadge = false,
  // CON-1925 — multi-attachment mode (direct API only). maxAttachments > 1
  // lets the picker take several files at once (onFileChange then carries
  // `fileUploads`, every picked file, beside the first in `fileUpload`) and
  // renders `additionalAttachments` (blob names, same container as the
  // first) under the main file, each with a remove that calls
  // onRemoveAdditional(index). The default of 1 is the component every
  // other parent has always had.
  maxAttachments = 1,
  additionalAttachments = [],
  onRemoveAdditional = null,
  // CON-1934 — where an extra lives, when it is not the first file's
  // container (a file just added to a definition whose first file is in
  // another account's). Default: the first file's container, as before.
  additionalAttachmentContainerOf = null,
  // CON-1930 — files still being compressed and uploaded: one placeholder
  // tile each, in the row with the previews. 0 everywhere outside the beta.
  pendingCount = 0,
}) {
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [localPendingFileUpload, setLocalPendingFileUpload] = useState(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [fileSizeBytes, setFileSizeBytes] = useState(null);
  const fileInputRef = useRef(null);

  // Determine which API mode we're using
  // Direct mode: onFileChange callback is provided
  // Legacy mode: updateFileUploadToMessage callback is provided
  const isDirectMode = onFileChange !== null;
  const isLegacyMode = !isDirectMode && (updateFileUploadToMessage !== null || fileName !== null);

  // PB (Project Broadcast) users can only send images - no contact cards or other file types
  const isPBUser = textingProvider === 'PB';
  const imageOnlyMode = imageOnly || isPBUser;
  const effectiveShowContactCardOption = imageOnlyMode ? false : showContactCardOption;
  const effectiveAcceptedFileTypes = imageOnlyMode ? (isPBUser ? PB_ACCEPTED_FILE_TYPES : '.jpg,.jpeg,.png,.gif,.webp') : acceptedFileTypes;
  const effectiveButtonSmallText = imageOnlyMode
    ? 'Large images will be compressed.'
    : buttonSmallText;

  // Normalize attachment name from either API
  // Direct mode uses 'attachment', legacy mode uses 'fileName'
  const effectiveAttachment = isDirectMode ? attachment : fileName;
  const effectiveContainer = isDirectMode ? attachmentContainer : container;

  // Build the current attachment object for display
  let currentAttachment = null;
  if (effectiveAttachment || fileUpload) {
    // If there's a pending fileUpload, use that file's name; otherwise use saved attachment
    const displayFileName = fileUpload?.file?.name || effectiveAttachment || '';
    currentAttachment = {
      fileName: displayFileName,
      container: effectiveContainer,
      isContactCard: isContactCardAttachment(effectiveAttachment),
      fileUpload,
    };
  }

  // Determine the pending file upload source
  const pendingFileUpload = fileUpload || localPendingFileUpload;
  const hasAttachment = currentAttachment !== null;
  const isContactCard = currentAttachment?.isContactCard === true;
  // CON-1925 — in multi-attachment mode "Attach File" ADDS while there is
  // room; the parent decides add vs replace from how many it already holds.
  // CON-1934 — the contact card is one of the files, so a file can be added
  // beside it too.
  const canAddAnotherFile = maxAttachments > 1
    && (1 + additionalAttachments.length) < maxAttachments;
  // CON-1934 — the same room, for the card beside a real file.
  const canAddContactCard = canAddAnotherFile && hasAttachment && !isContactCard;

  // CON-1674 — recorder state. No charge disclosure here: every surface that
  // renders AttachmentField sends through an automated send method that
  // already charges the automated per-text rate, so a notice would restate a
  // charge users already accept.
  // Connections is the only place the charge is new, and it uses a different
  // menu (InputStartAdornment).
  const [isRecordingVoiceMemo, setIsRecordingVoiceMemo] = useState(false);

  const handleOpenPopover = (event) => {
    setPopoverAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setPopoverAnchorEl(null);
  };

  const validateFileInput = (uploadFileData) => {
    if (!uploadFileData) {
      setErrorMessage(null);
      return true;
    }
    // Skip size validation for compressible images - compression will handle it
    const isImage = uploadFileData?.file && isCompressibleImage(uploadFileData.file);
    if (!isImage && !helpers.azure.validateFileSize(uploadFileData)) {
      setErrorMessage(validateMessage.validateFileSize || 'File is too large');
      return false;
    }
    if (!helpers.azure.validateFileType(uploadFileData)) {
      setErrorMessage(validateMessage.validateFileType || 'Invalid file type');
      return false;
    }
    setErrorMessage(null);
    return true;
  };

  const handleAttachFile = () => {
    handleClosePopover();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAttachContactCard = () => {
    handleClosePopover();
    // CON-1934 — read before anything changes: room left means the parent
    // ADDS the card and keeps every file (it becomes attachment 1).
    const addContactCard = canAddContactCard;
    const contactCardAttachment = {
      fileName: VCARD_FILENAME,
      container: null,
      isContactCard: true,
    };

    if (isDirectMode && onFileChange) {
      // Direct mode: call onFileChange with the new state
      onFileChange({
        attachment: VCARD_FILENAME,
        attachmentContainer: null,
        fileUpload: null,
        isContactCard: true,
        ...(addContactCard ? { addContactCard: true } : {}),
      });
    } else if (isLegacyMode && updateFileUploadToMessage) {
      // Legacy mode: call the updateFileUploadToMessage with a special marker
      // The parent component should handle setting the fileName to VCARD_FILENAME
      updateFileUploadToMessage({ isContactCard: true, fileName: VCARD_FILENAME });
    } else if (onChange) {
      onChange([contactCardAttachment]);
    }
    setLocalPendingFileUpload(null);
    onAttachmentChange([contactCardAttachment]);
  };

  const handleFileSelected = (event) => {
    const { target: { files, value: inputValue } } = event;
    if (!files || !inputValue || files.length === 0) {
      return;
    }

    const uploadFileData = {
      file: files[0],
      value: inputValue,
    };
    // CON-1925 — every picked file, for a multi-attachment parent. With
    // maxAttachments 1 the input has no `multiple`, so this is one entry.
    const fileUploads = Array.from(files)
      .slice(0, Math.max(1, maxAttachments))
      .map((file) => ({ file, value: inputValue }));

    if (fileUploads.every((entry) => validateFileInput(entry))) {
      if (isDirectMode && onFileChange) {
        // Direct mode: call onFileChange with the new state
        // Note: We clear the old attachment name since a new file is being uploaded
        // The parent should track fileUpload in Redux for form submission
        onFileChange({
          attachment: null, // Clear attachment - new file will be uploaded on save
          attachmentContainer: null, // Reset container for new file
          fileUpload: uploadFileData,
          fileUploads,
          isContactCard: false,
        });
        // CON-1930 — adding to files already attached (beta): the parent
        // shows placeholders and then the new set, so the main preview must
        // not switch to the picked file meanwhile.
        // CON-1934 — the contact card counts: a file picked beside it is added.
        if (!(maxAttachments > 1 && hasAttachment)) {
          setLocalPendingFileUpload(uploadFileData);
        }
      } else if (isLegacyMode && updateFileUploadToMessage) {
        // Legacy mode: call the existing callback with the file upload data
        updateFileUploadToMessage(uploadFileData);
      } else if (onChange) {
        const newAttachment = {
          fileName: files[0].name,
          container: null,
          isContactCard: false,
          fileUpload: uploadFileData,
        };
        onChange([newAttachment]);
        setLocalPendingFileUpload(uploadFileData);
        onAttachmentChange([newAttachment]);
      }
    }

    // Reset the input so the same file can be selected again
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
  };

  /* CON-1674 — a recorded memo takes the identical path a picked file takes,
   * so every mode (direct, legacy, onChange) behaves the same as it always
   * has. Only the origin of the File differs. */
  const handleVoiceMemoRecorded = (file) => {
    setIsRecordingVoiceMemo(false);
    const uploadFileData = { file, value: file.name };
    if (!validateFileInput(uploadFileData)) return;

    if (isDirectMode && onFileChange) {
      onFileChange({
        attachment: null,
        attachmentContainer: null,
        fileUpload: uploadFileData,
        isContactCard: false,
      });
      setLocalPendingFileUpload(uploadFileData);
    } else if (isLegacyMode && updateFileUploadToMessage) {
      updateFileUploadToMessage(uploadFileData);
    } else if (onChange) {
      const newAttachment = {
        fileName: file.name,
        container: null,
        isContactCard: false,
        fileUpload: uploadFileData,
      };
      onChange([newAttachment]);
      setLocalPendingFileUpload(uploadFileData);
      onAttachmentChange([newAttachment]);
    }
  };

  const handleRemoveAttachment = () => {
    if (isDirectMode && onFileChange) {
      // Direct mode: call onFileChange with null values
      onFileChange({
        attachment: null,
        attachmentContainer: null,
        fileUpload: null,
        isContactCard: false,
      });
    } else if (isLegacyMode && updateFileUploadToMessage) {
      // Legacy mode: call with null to remove
      updateFileUploadToMessage(null);
    } else if (onChange) {
      onChange([]);
    }
    setLocalPendingFileUpload(null);
    setErrorMessage(null);
    onAttachmentChange([]);
  };

  const getFilePreviewUrl = () => {
    if (!currentAttachment) return null;

    if (isContactCard) {
      return null; // Contact cards don't have image previews
    }

    // If there's a pending file upload, show preview from the File object
    if (pendingFileUpload?.file) {
      return URL.createObjectURL(pendingFileUpload.file);
    }

    // If there's an existing attachment filename, show from Azure
    if (currentAttachment.fileName && !currentAttachment.isContactCard) {
      return helpers.azure.getImageFileURL(
        currentAttachment.fileName,
        userId,
        azAccountName,
        STORAGE.sas,
        databaseBeingUsed,
        effectiveContainer,
        true // addSas - required for image to load
      );
    }

    return null;
  };

  const isImageFile = (name) => {
    if (!name) return false;
    const ext = name.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  };

  const previewUrl = getFilePreviewUrl();
  const showImagePreview = previewUrl && isImageFile(currentAttachment?.fileName) && !imageLoadError;

  // Reset image load error when attachment changes
  React.useEffect(() => {
    setImageLoadError(false);
  }, [currentAttachment?.fileName]);

  // After save, the parent provides a new fileName/attachment — clear local pending state
  // so the size badge switches from "Save to verify size" to the actual HEAD-request size
  useEffect(() => {
    if (effectiveAttachment && localPendingFileUpload) {
      setLocalPendingFileUpload(null);
    }
  }, [effectiveAttachment]);

  // Fetch file size: from File object if just uploaded, or HEAD request for existing blobs
  // Pending compressible images skip size — compression will reduce them at send/save time
  // If effectiveAttachment is set, the file has been saved (server returned a filename),
  // so it's no longer pending even if fileUpload prop hasn't been cleared yet
  const isPendingCompressible = pendingFileUpload?.file
    && isCompressibleImage(pendingFileUpload.file)
    && !effectiveAttachment;

  useEffect(() => {
    setFileSizeBytes(null);

    if (!currentAttachment) return;

    // CON-1934 — in the multi-attachment beta the contact card counts toward
    // the total at its real size (about 70 KB with a photo), read from the
    // user's own container. Outside the beta a card shows no badge, as before.
    if (isContactCard) {
      if (!(maxAttachments > 1)) return;
      const cardUrl = helpers.azure.getImageFileURL(
        VCARD_FILENAME,
        userId,
        azAccountName,
        STORAGE.sas,
        databaseBeingUsed,
        null,
        true,
      );
      let cancelledCard = false;
      fetch(cardUrl, { method: 'HEAD' })
        .then((res) => {
          if (!cancelledCard) {
            const len = res.ok ? res.headers.get('content-length') : null;
            setFileSizeBytes(len ? Number(len) : 0);
          }
        })
        .catch(() => {});
      // eslint-disable-next-line consistent-return
      return () => { cancelledCard = true; };
    }

    // For pending compressible images, skip — size will change after compression
    if (isPendingCompressible) return;

    // If there's a pending non-compressible file that hasn't been saved yet, use its size
    // Once effectiveAttachment is set, the file has been saved — skip to HEAD request
    if (pendingFileUpload?.file?.size && !effectiveAttachment) {
      setFileSizeBytes(pendingFileUpload.file.size);
      return;
    }

    // For existing (saved) attachments, HEAD request the blob URL to get final size
    // Use effectiveAttachment (server-side blob name) rather than currentAttachment.fileName
    // which may still be the local file name if fileUpload prop hasn't been cleared
    const blobFileName = effectiveAttachment || currentAttachment.fileName;
    if (blobFileName) {
      const blobUrl = helpers.azure.getImageFileURL(
        blobFileName,
        userId,
        azAccountName,
        STORAGE.sas,
        databaseBeingUsed,
        effectiveContainer,
        true,
      );
      if (blobUrl) {
        let cancelled = false;
        fetch(blobUrl, { method: 'HEAD' })
          .then((res) => {
            if (!cancelled) {
              const len = res.headers.get('content-length');
              if (len) setFileSizeBytes(Number(len));
            }
          })
          .catch(() => {});
        // eslint-disable-next-line consistent-return
        return () => { cancelled = true; };
      }
    }
  }, [effectiveAttachment, currentAttachment?.fileName, pendingFileUpload?.file]);

  const fileSizeKB = fileSizeBytes != null ? Math.round(fileSizeBytes / 1024) : null;
  const isFileTooLarge = fileSizeKB != null && fileSizeKB > MMS_SIZE_LIMIT_KB;

  /* CON-1929 — every image on the message is shown at full preview size,
   * side by side, rather than the first as a picture and the rest as chips.
   * Other file types (pdf, audio, ...) keep their chip under the row. */
  const extraUrl = (name) => helpers.azure.getImageFileURL(
    name,
    userId,
    azAccountName,
    STORAGE.sas,
    databaseBeingUsed,
    additionalAttachmentContainerOf ? additionalAttachmentContainerOf(name) : effectiveContainer,
    true,
  );
  const extras = maxAttachments > 1 ? additionalAttachments.map((name, index) => ({ name, index })) : [];
  const extraImages = extras.filter(({ name }) => isImageFile(name));
  const extraFiles = extras.filter(({ name }) => !isImageFile(name));
  // On a phone three 208 px previews would stack one per row; there they
  // shrink to a third of the width each so the whole set stays in one row.
  const isNarrowScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const compactTiles = isNarrowScreen && extraImages.length > 0;
  const tileOuterStyle = compactTiles ? { width: 'calc((100% - 16px) / 3)' } : {};
  const tileInnerStyle = compactTiles ? { width: '100%', height: 'auto', aspectRatio: '208 / 152' } : {};

  // CON-1929 — with several files the badge shows the message total, which is
  // what the budget is about, instead of the first file alone.
  const [extrasBytes, setExtrasBytes] = useState(0);
  const extrasKey = extras.map(({ name }) => name).join('|');
  useEffect(() => {
    let cancelled = false;
    if (!extras.length) {
      setExtrasBytes(0);
      return undefined;
    }
    Promise.all(extras.map(({ name }) => (
      fetch(extraUrl(name), { method: 'HEAD' })
        .then((res) => Number(res.headers.get('content-length')) || 0)
        .catch(() => 0)
    ))).then((sizes) => {
      if (!cancelled) setExtrasBytes(sizes.reduce((sum, bytes) => sum + bytes, 0));
    });
    return () => { cancelled = true; };
  }, [extrasKey, effectiveContainer]);
  // CON-1934 — a card with files behind it shows the whole total, the card
  // at its real size (the server measures it the same way); until its size
  // is read the total is the files alone.
  const totalKB = (fileSizeKB != null || isContactCard) && extras.length
    ? Math.round(((fileSizeBytes || 0) + extrasBytes) / 1024)
    : null;

  const handleImageError = () => {
    setImageLoadError(true);
  };

  return (
    <Box className={classes.attachmentFieldContainer}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        accept={effectiveAcceptedFileTypes}
        type="file"
        multiple={maxAttachments > 1}
        className={classes.hiddenInput}
        onChange={handleFileSelected}
        disabled={loading}
      />

      {/* CON-1674 — recording replaces the drop area, so the field shows one
          thing at a time rather than a recorder floating beside a dropzone. */}
      {isRecordingVoiceMemo && (
        <Box sx={{ mb: 1 }}>
          <VoiceRecorder
            onRecorded={handleVoiceMemoRecorded}
            onCancel={() => setIsRecordingVoiceMemo(false)}
            onError={() => setIsRecordingVoiceMemo(false)}
          />
        </Box>
      )}

      {/* Title label */}
      {title && (
        <Box className={classes.titleLabel}>
          {title}
        </Box>
      )}

      {/* Main attachment area */}
      <Box className={classes.attachmentArea}>
        {!hasAttachment && pendingCount > 0 ? null : !hasAttachment ? (
          /* Empty state - show upload button with menu */
          <Box className={classes.uploadButtonContainer}>
            <Button
              className={classes.uploadButton}
              variant="contained"
              component="span"
              disabled={loading}
              onClick={handleOpenPopover}
            >
              <SvgIcon
                className="svgIcon"
                component={UploadIcon}
                viewBox="0 0 32 22"
              />
              <div className="buttonName">
                {title || 'Attach File'}
              </div>
              <div className="buttonSmallText">
                {effectiveButtonSmallText}
              </div>
            </Button>
          </Box>
        ) : isContactCard ? (
          /* Contact Card attached - clickable to show menu */
          <Box
            className={classes.contactCardContainer}
            onClick={handleOpenPopover}
            sx={{ cursor: 'pointer' }}
          >
            <Box className={classes.contactCardContent}>
              <ContactPageIcon className={classes.contactCardIcon} />
              <Box className={classes.contactCardText}>
                <Typography variant="body2" fontWeight={500}>
                  Your Contact Card
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {VCARD_FILENAME}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          /* File attached - clickable to show menu */
          <Box
            className={classes.fileAttachedContainer}
            onClick={handleOpenPopover}
            sx={{ cursor: 'pointer' }}
            style={tileOuterStyle}
          >
            {showImagePreview ? (
              <Box className={classes.imagePreviewContainer} style={tileInnerStyle}>
                <img
                  src={previewUrl}
                  alt={stripDatetimePrefix(currentAttachment.fileName)}
                  className={classes.imagePreview}
                  onError={handleImageError}
                />
              </Box>
            ) : (
              <Box className={classes.fileInfoContainer}>
                {getFileIcon(currentAttachment.fileName, classes)}
                <Typography variant="body2" noWrap className={classes.fileName}>
                  {stripDatetimePrefix(currentAttachment.fileName)}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {hasAttachment && extraImages.map(({ name, index }) => (
          <Box
            key={name}
            className={classes.imagePreviewContainer}
            style={{
              position: 'relative', ...tileInnerStyle, ...tileOuterStyle,
            }}
          >
            <img
              src={extraUrl(name)}
              alt={stripDatetimePrefix(name)}
              className={classes.imagePreview}
            />
            {onRemoveAdditional && !loading && (
              <IconButton
                size="small"
                aria-label={`Remove ${stripDatetimePrefix(name)}`}
                onClick={() => onRemoveAdditional(index)}
                style={{
                  position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(255,255,255,0.9)', padding: 2,
                }}
              >
                <DeleteForeverIcon style={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>
        ))}
        {Array.from({ length: pendingCount }, (_, index) => (
          <AttachmentPendingTile
            key={`pending-${index}`}
            width={208}
            height={152}
            style={compactTiles || isNarrowScreen ? {
              width: 'calc((100% - 16px) / 3)', height: 'auto', aspectRatio: '208 / 152',
            } : {}}
          />
        ))}
      </Box>

      {/* CON-1925 — the 2nd..Nth files, under the main one, each removable. */}
      {extraFiles.length > 0 && (
        <Box
          style={{
            marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6,
          }}
        >
          {extraFiles.map(({ name, index }) => (
            <Chip
              key={name}
              size="small"
              icon={getFileIcon(name, classes)}
              label={stripDatetimePrefix(name)}
              onDelete={onRemoveAdditional && !loading ? () => onRemoveAdditional(index) : undefined}
              deleteIcon={<DeleteForeverIcon />}
              style={{ maxWidth: 260, fontSize: 11 }}
            />
          ))}
        </Box>
      )}
      {maxAttachments > 1 && hasAttachment && (
        <Typography
          variant="caption"
          style={{
            color: '#888', fontSize: 11, display: 'block', marginTop: 4
          }}
        >
          {`${1 + additionalAttachments.length} of ${maxAttachments} attachments`}
        </Typography>
      )}

      {/* File size badge — hidden for form/page images where KB limit doesn't apply */}
      {hasAttachment && (!isContactCard || extras.length > 0) && !hideSizeBadge && (
        <Box style={{
          marginTop: 4, display: 'flex', alignItems: 'center', gap: 4,
        }}
        >
          {isPendingCompressible ? (
            <Typography
              variant="caption"
              style={{ color: '#888', fontSize: 11, fontStyle: 'italic' }}
            >
              {compressOnSend ? 'Will be compressed on send' : 'Save to verify size'}
            </Typography>
          ) : isFileTooLarge ? (
            <Tooltip
              title="Attachments over 300KB may not be delivered by some carriers. Please re-upload this image to allow CP to compress it."
              arrow
            >
              <Chip
                size="small"
                icon={<InfoOutlinedIcon style={{ fontSize: 14 }} />}
                label={`${fileSizeKB} KB - Too Large`}
                style={{
                  backgroundColor: '#FFF3E0',
                  color: '#E65100',
                  border: '1px solid #FFB74D',
                  fontWeight: 500,
                  fontSize: 11,
                  height: 22,
                }}
              />
            </Tooltip>
          ) : (fileSizeKB != null || totalKB != null) ? (
            <Chip
              size="small"
              icon={<CheckCircleOutlineIcon style={{ fontSize: 14 }} />}
              label={totalKB != null ? `${totalKB} KB total` : `${fileSizeKB} KB`}
              style={{
                backgroundColor: '#E8F5E9',
                color: '#2E7D32',
                border: '1px solid #A5D6A7',
                fontWeight: 500,
                fontSize: 11,
                height: 22,
              }}
            />
          ) : null}
        </Box>
      )}

      {/* Popover menu */}
      <Popover
        open={!!popoverAnchorEl}
        anchorEl={popoverAnchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          root: {
            sx: {
              '& .MuiMenuItem-root': {
                padding: '8px 6px',
                borderRadius: '4px',
                marginBottom: '4px',
                '&:last-child': { marginBottom: 0 },
              },
            },
          },
          paper: {
            sx: {
              borderRadius: '12px',
              padding: '8px',
            },
          },
        }}
      >
        <ActionMenuItem
          disabled={loading}
          onClick={handleAttachFile}
          icon={<AttachFileIcon fontSize="small" />}
          label={hasAttachment
            ? (imageOnly ? 'Replace Image' : (canAddAnotherFile ? 'Add Another File' : 'Replace with File'))
            : (imageOnly ? 'Upload Image' : 'Attach File')}
        />
        {effectiveShowContactCardOption && !isContactCard && (
          <ActionMenuItem
            disabled={loading}
            onClick={handleAttachContactCard}
            icon={<ContactPageIcon fontSize="small" />}
            label={contactCardMenuLabel({ hasAttachment, canAdd: canAddContactCard })}
          />
        )}
        {/* CON-1674 — off on these surfaces; see the note at the top of the
            file. Also hidden where the browser cannot record, and on
            image-only fields, rather than shown disabled. */}
        {VOICE_MEMO_ON_AUTOMATED_SURFACES && !imageOnly && isRecordingSupported() && (
          <ActionMenuItem
            disabled={loading}
            onClick={() => { handleClosePopover(); setIsRecordingVoiceMemo(true); }}
            icon={<MicIcon fontSize="small" />}
            label={hasAttachment ? 'Replace with Voice Memo' : 'Record a Voice Memo'}
          />
        )}
        {hasAttachment && (
          <ActionMenuItem
            disabled={loading}
            onClick={() => {
              handleClosePopover();
              handleRemoveAttachment();
            }}
            icon={<DeleteForeverIcon fontSize="small" />}
            label="Remove"
          />
        )}
      </Popover>

      {/* Error message */}
      {(errorValue || errorMessage) && (
        <Box className={classes.errorMessage}>
          {errorValue || errorMessage}
        </Box>
      )}
    </Box>
  );
}

AttachmentField.propTypes = {
  title: PropTypes.string,
  userId: PropTypes.number.isRequired,
  azAccountName: PropTypes.string.isRequired,
  databaseBeingUsed: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  validateMessage: PropTypes.object,
  // Direct use API (preferred)
  attachment: PropTypes.string,
  attachmentContainer: PropTypes.string,
  onFileChange: PropTypes.func,
  // Legacy props for backward compatibility (deprecated)
  fileName: PropTypes.string,
  fileUpload: PropTypes.any,
  container: PropTypes.string,
  updateFileUploadToMessage: PropTypes.func,
  // Unused legacy props (kept for backward compatibility)
  value: PropTypes.arrayOf(
    PropTypes.shape({
      fileName: PropTypes.string,
      container: PropTypes.string,
      isContactCard: PropTypes.bool,
      fileUpload: PropTypes.object,
    })
  ),
  onChange: PropTypes.func,
  // Common props
  compressOnSend: PropTypes.bool,
  textingProvider: PropTypes.string,
  showContactCardOption: PropTypes.bool,
  imageOnly: PropTypes.bool,
  acceptedFileTypes: PropTypes.string,
  errorValue: PropTypes.any,
  classes: PropTypes.object.isRequired,
  buttonSmallText: PropTypes.string,
  onAttachmentChange: PropTypes.func,
  hideSizeBadge: PropTypes.bool,
  maxAttachments: PropTypes.number,
  additionalAttachments: PropTypes.arrayOf(PropTypes.string),
  additionalAttachmentContainerOf: PropTypes.func,
  onRemoveAdditional: PropTypes.func,
  pendingCount: PropTypes.number,
};

export default withStyles(AttachmentField, attachmentFieldStyle);

export { VCARD_FILENAME, isContactCardAttachment };
